/* Loupe – desktop gallery layouts for the product page (thumbs left, thumbs below, slider).
   The track [data-gallery-track] is a horizontal scroll-snap slider on every screen size in these layouts, so the
   theme's <loupe-gallery> keeps doing what it already does (thumb clicks scroll the track, the active thumb and the
   counter follow the scroll position, [data-zoom] opens the lightbox). This element only adds arrows, a zoom button
   for the current image, keyboard control and the vertical thumb rail. It lives inside [data-gallery], which the
   variant picker swaps via the Section Rendering API – connectedCallback re-initialises it every time. */
(() => {
  if (customElements.get('loupe-gallery-controls')) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 990px)');

  class LoupeGalleryControls extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-gallery]');
      this.track = this.root && this.root.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.items = [...this.track.children];
      this.thumbs = [...this.root.querySelectorAll('[data-gallery-go]')];
      this.rail = this.thumbs.length ? this.thumbs[0].parentElement : null;
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.zoom = this.querySelector('[data-gallery-zoom]');
      this.status = this.querySelector('[data-gallery-status]');
      this.i = -1;

      this.onScroll = () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.sync()); };
      this.onKey = (e) => this.key(e);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.root.addEventListener('keydown', this.onKey);
      this.prev && this.prev.addEventListener('click', () => this.go(this.i - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.i + 1));
      this.sync(true);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.track && this.track.removeEventListener('scroll', this.onScroll);
      this.root && this.root.removeEventListener('keydown', this.onKey);
    }

    index() {
      const step = this.items.length > 1 ? this.items[1].offsetLeft - this.items[0].offsetLeft : 0;
      if (!step) return 0;
      return Math.max(0, Math.min(this.items.length - 1, Math.round(Math.abs(this.track.scrollLeft) / step)));
    }

    go(n, focus) {
      const last = this.items.length - 1;
      n = Math.max(0, Math.min(last, n));
      const it = this.items[n]; if (!it) return;
      this.track.scrollTo({ left: it.offsetLeft - this.items[0].offsetLeft, behavior: reduce.matches ? 'auto' : 'smooth' });
      if (focus) it.focus({ preventScroll: true });
    }

    sync(initial) {
      const i = this.index();
      if (i === this.i) return;
      this.i = i;
      const last = this.items.length - 1;
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= last;
      /* the zoom button always opens the image that is showing (videos and 3D models have none) */
      if (this.zoom) {
        const z = this.items[i] && this.items[i].dataset.zoom;
        this.zoom.hidden = !z;
        if (z) this.zoom.dataset.zoom = z;
      }
      if (this.status && !initial && this.status.dataset.template) {
        this.status.textContent = this.status.dataset.template.replace('[n]', i + 1);
      }
      /* vertical rail: keep the active thumbnail in view */
      if (this.rail && desktop.matches && this.root.classList.contains('gallery-wrap--d-thumbs-left')) {
        const t = this.thumbs[i];
        if (t && this.rail.scrollHeight > this.rail.clientHeight) {
          this.rail.scrollTo({ top: t.offsetTop - this.rail.clientHeight / 2 + t.offsetHeight / 2, behavior: reduce.matches || initial ? 'auto' : 'smooth' });
        }
      }
    }

    key(e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.target.closest && e.target.closest('video, iframe, model-viewer, input, select, textarea')) return;   // media controls keep their keys
      const inTrack = this.track.contains(e.target);
      const thumb = e.target.closest && e.target.closest('[data-gallery-go]');
      if (!inTrack && !thumb) return;
      const back = ['ArrowLeft', 'ArrowUp'], fwd = ['ArrowRight', 'ArrowDown'];
      if (inTrack && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;   // keep page scrolling on the image
      let n = null;
      const from = thumb ? +thumb.dataset.galleryGo : this.index();
      if (back.includes(e.key)) n = from - 1;
      else if (fwd.includes(e.key)) n = from + 1;
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = this.items.length - 1;
      if (n === null) return;
      n = Math.max(0, Math.min(this.items.length - 1, n));
      e.preventDefault();
      if (thumb) {
        const t = this.thumbs[n];
        if (t) { t.focus(); t.click(); }
      } else this.go(n, true);
    }
  }

  customElements.define('loupe-gallery-controls', LoupeGalleryControls);
})();
